import { useEffect, useMemo, useRef, useState } from 'react';
import { SwitchCase } from '@toss/react';
import '../events.quiz-image-search';
import { useTrackEvent } from '@zep/logger/react';
import { useCountryCode } from '@/shared/hooks';
import { useImageSearch } from '../hooks/useImageSearch';
import { useSearchedImageFile } from '../hooks/useSearchedImageFile';
import type { ImageSearchItem, ImageSearchProvider, ImageUploadModalPlace } from '../types/imageSearch.types';
import { SearchDefaultScreen } from './SearchDefaultScreen';
import { SearchedImagesScreen } from './SearchedImagesScreen';
import { SearchInput, type SearchInputRef } from './SearchInput';
import { SearchLoadingScreen } from './SearchLoadingScreen';
import { SearchNotFoundScreen } from './SearchNotFoundScreen';
type ImageSearchScreenType = 'loading' | 'images' | 'notFound' | 'default';

// Custom hook for scroll and focus management
const useSearchBehaviors = (submittedSearch: string, shouldFocusOnReturn: boolean, setShouldFocusOnReturn: (value: boolean) => void, scrollContainerRef: React.RefObject<HTMLDivElement>, searchInputRef: React.RefObject<SearchInputRef>) => {
  useEffect(() => {
    if (submittedSearch && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [submittedSearch, scrollContainerRef]);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && shouldFocusOnReturn) {
        searchInputRef.current?.focus();
        setShouldFocusOnReturn(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [shouldFocusOnReturn, setShouldFocusOnReturn, searchInputRef]);
};

// Custom hook for search handlers
const useSearchHandlers = (provider: ImageSearchProvider, place: ImageUploadModalPlace, setSubmittedSearch: (value: string) => void, setKeyWordChanged: (value: boolean) => void, onUploadStart: (files: File[]) => void, getSearchedImageFile: (imageFetchUrl: string) => Promise<File | null>) => {
  const {
    track
  } = useTrackEvent();
  const handleSearch = (searchValue: string) => {
    const trimmedSearchValue = searchValue.trim();
    if (!trimmedSearchValue) return;
    setSubmittedSearch(trimmedSearchValue);
    setKeyWordChanged(false);
    track('image_search', {
      keyword: trimmedSearchValue,
      provider,
      place
    });
  };
  const handleImageSelect = async (image: ImageSearchItem, submittedSearch: string) => {
    const file = await getSearchedImageFile(image.imageFetchUrl);
    if (file) {
      // 검색은 이미지 한 장을 고르므로 다중 업로드 계약(File[])에 맞춰 배열로 감싼다.
      onUploadStart([file]);
    }
    track('searched_image_select', {
      keyword: submittedSearch,
      provider,
      place
    });
  };
  return {
    handleSearch,
    handleImageSelect
  };
};
export const SearchTabScreen = ({
  provider,
  place,
  onUploadStart,
  imageUrl,
  multiple = false
}: {
  provider: ImageSearchProvider;
  place: ImageUploadModalPlace;
  onUploadStart: (files: File[]) => void;
  imageUrl?: string;
  multiple?: boolean;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<SearchInputRef>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [keyWordChanged, setKeyWordChanged] = useState<boolean>(false);
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [shouldFocusOnReturn, setShouldFocusOnReturn] = useState(false);
  const {
    countryCode
  } = useCountryCode();

  // provider와 무관하게 서버 통합 검색 API 하나만 사용한다.
  // 정규화(필터링·출처 표기·프록시 토큰 부착)는 전부 서버가 담당한다.
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError
  } = useImageSearch({
    provider,
    searchQuery: submittedSearch,
    countryCode,
    enabled: true
  });
  const images = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page?.items ?? []);
  }, [data]);
  const getSearchedImageFile = useSearchedImageFile();
  const {
    handleSearch,
    handleImageSelect
  } = useSearchHandlers(provider, place, setSubmittedSearch, setKeyWordChanged, onUploadStart, getSearchedImageFile);
  const handleLinkClick = () => {
    setShouldFocusOnReturn(true);
  };
  const currentSearchStatus = useMemo<ImageSearchScreenType>(() => {
    const showLoadingState = isLoading;
    const hasImages = images.length > 0;
    const showNotFound = !isLoading && !isError && submittedSearch && !hasImages;
    if (showLoadingState) return 'loading';
    if (hasImages) return 'images';
    if (showNotFound) return 'notFound';
    return 'default';
  }, [isLoading, isError, submittedSearch, images.length]);
  useSearchBehaviors(submittedSearch, shouldFocusOnReturn, setShouldFocusOnReturn, scrollContainerRef, searchInputRef);
  return <div className="flex h-full flex-col" data-sentry-component="SearchTabScreen" data-sentry-source-file="SearchTabScreen.tsx">
      <SearchInput ref={searchInputRef} value={searchValue} onChange={value => {
      setSearchValue(value);
      setKeyWordChanged(true);
    }} onSearch={() => handleSearch(searchValue)} isSearchButtonVisible={!submittedSearch && !isLoading || keyWordChanged} data-sentry-element="SearchInput" data-sentry-source-file="SearchTabScreen.tsx" />
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        <SwitchCase value={currentSearchStatus} defaultComponent={<SearchDefaultScreen provider={provider} onLinkClick={handleLinkClick} />} caseBy={{
        loading: <SearchLoadingScreen />,
        images: <SearchedImagesScreen images={images} isLoadingMore={isFetchingNextPage} hasNextPage={hasNextPage} onImageSelect={image => handleImageSelect(image, submittedSearch)} onLoadMore={fetchNextPage} />,
        notFound: <SearchNotFoundScreen onUploadStart={onUploadStart} imageUrl={imageUrl} multiple={multiple} />
      }} data-sentry-element="SwitchCase" data-sentry-source-file="SearchTabScreen.tsx" />
      </div>
    </div>;
};